/**
 * Fire Report Handler - FIXED TABLE STRUCTURE VERSION
 * ✅ Maintains strict 5-column layout throughout
 * ✅ Proper column widths matching WebUI
 * ✅ Handles DD/MM/YYYY date format
 * ✅ Works with empty fire_report array
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import govtLogo from '../Govt_logo.png';

const loadLogo = (src) =>
  new Promise((resolve, reject) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.src = src;
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
  });

const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) return dateStr;
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }
  if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) return dateStr.replace(/-/g, '/');
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (e) {
    return '';
  }
};

const formatDateToDDMMYYYY_Dash = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) return dateStr.replace(/\//g, '-');
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year}`;
  }
  if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) return dateStr;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    return '';
  }
};

const generateISTFilename = (prefix) => {
  const now = new Date();
  const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
  const istDate = istTime.toISOString().split('T')[0];
  const istTimeStr = istTime.toISOString().split('T')[1].split('.')[0].replace(/:/g, '');
  return `${prefix}_${istDate}_${istTimeStr}`;
};

const extractDistrictName = (rawData, rows) => {
  let districtName = 'NA';
  if (rawData?.district_name) {
    districtName = String(rawData.district_name).trim().toUpperCase();
  } else if (rawData?.districtname) {
    districtName = String(rawData.districtname).trim().toUpperCase();
  } else if (rawData?.district) {
    districtName = String(rawData.district).trim().toUpperCase();
  } else if (rows?.length > 0 && rows[0]?.dist_name) {
    districtName = String(rows[0].dist_name).trim().toUpperCase();
  } else if (rows?.length > 0 && rows[0]?.district_name) {
    districtName = String(rows[0].district_name).trim().toUpperCase();
  } else if (rows?.length > 0 && rows[0]?.district) {
    districtName = String(rows[0].district).trim().toUpperCase();
  }
  return districtName;
};

const extractBlockName = (rawData, rows) => {
  let blockName = 'NA';
  if (rawData?.block_name) {
    blockName = String(rawData.block_name).trim();
  } else if (rawData?.blockname) {
    blockName = String(rawData.blockname).trim();
  } else if (rawData?.block) {
    blockName = String(rawData.block).trim();
  } else if (rows?.length > 0 && rows[0]?.block_name) {
    blockName = String(rows[0].block_name).trim();
  } else if (rows?.length > 0 && rows[0]?.blockname) {
    blockName = String(rows[0].blockname).trim();
  }
  return blockName || 'NA';
};

const getSafeValue = (value) => {
  return (value !== null && value !== undefined && value !== '') ? String(value) : '';
};

const getSafeStringValue = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return 'NA';
  }
  return String(value).trim();
};

/**
 * MAIN: Handle Fire Daily Report PDF Generation
 * ✅ FIXED: Proper 5-column table structure
 */
export const handleFireDailyReportPDF = async (message, format, rawData) => {
  console.log('🔥 Fire Daily Report PDF generation started');
  console.log('📊 Raw Data:', JSON.stringify(rawData, null, 2));
  
  if (format !== 'pdf') {
    console.warn('⚠️ Unsupported format for fire daily report:', format);
    alert('Fire Daily Report only supports PDF format');
    return;
  }
  
  try {
    const doc = new jsPDF('p', 'pt', 'a4');
    
    const reportDate =
      rawData?.reported_on ||
      rawData?.reported_on_iso ||
      message.debug?.tool_result?.parameters?.reported_on ||
      message.debug?.parameters?.reported_on ||
      new Date().toISOString().split('T')[0];
    
    const displayDate = formatDateToDDMMYYYY(reportDate);
    const headerDisplayDate = formatDateToDDMMYYYY_Dash(reportDate);
    
    console.log('📅 Report Date:', displayDate);
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const centerX = pageWidth / 2;
    
    // Load logo
    let logoImg = null;
    try {
      logoImg = await loadLogo(govtLogo);
    } catch (e) {
      console.warn('⚠️ Logo load failed:', e.message);
    }
    
    if (logoImg) {
      const logoWidth = 60;
      const logoHeight = 60;
      const logoX = centerX - logoWidth / 2;
      doc.addImage(logoImg, 'JPEG', logoX, 55, logoWidth, logoHeight);
    }
    
    // Header section
    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(13);
    doc.text('Govt. of ABC State', centerX, 120, { align: 'center' });
    doc.text('Department Name', centerX, 140, { align: 'center' });
    doc.text(`Fire Daily Report (Dated on ${displayDate})`, centerX, 160, { align: 'center' });
    
    // ✅ Merge fire_report and fire_report_cum data
    let mergedReport = {};
    
    if (rawData?.fire_report && rawData.fire_report.length > 0) {
      console.log('✅ Merging fire_report (Today data)');
      rawData.fire_report.forEach((report) => {
        mergedReport = { ...mergedReport, ...report };
      });
    } else {
      console.warn('⚠️ fire_report is EMPTY');
    }
    
    if (rawData?.fire_report_cum && rawData.fire_report_cum.length > 0) {
      console.log('✅ Merging fire_report_cum (Cumulative data)');
      rawData.fire_report_cum.forEach((cumReport) => {
        mergedReport = { ...mergedReport, ...cumReport };
      });
    }
    
    // Handle address
    if (rawData?.fire_report_address && rawData.fire_report_address.length > 0) {
      const validAddresses = rawData.fire_report_address
        .map((entry) => entry.address)
        .filter((address) => address && address.trim() !== '');
      mergedReport.address = validAddresses.length > 0 ? validAddresses.join(', ') : 'NA';
    } else {
      mergedReport.address = mergedReport.address || 'NA';
    }
    
    const report = mergedReport;
    const districtName = extractDistrictName(rawData, [report]);
    const blockName = extractBlockName(rawData, [report]);
    
    console.log('📍 District:', districtName);
    console.log('📍 Block:', blockName);
    console.log('🔑 Report keys:', Object.keys(report));
    
    // ✅ FIXED: STRICT 5-COLUMN TABLE STRUCTURE
    // Column widths: [S.No, Label, SubLabel, Today, Cumulative]
    const tableData = [];
    
    // Header row - spans all 5 columns properly
    tableData.push([
      { content: `Name of the District: ${districtName}`, colSpan: 4, styles: { fontStyle: 'bold', halign: 'left' } },
      { content: `Date:\n${headerDisplayDate}`, styles: { fontStyle: 'bold', halign: 'left' } }
    ]);
    
    // Section 1: Block (5 columns: S.No | Label | Value spans 3 cols)
    tableData.push([
      '1',
      'Block',
      { content: blockName, colSpan: 3 }
    ]);
    
    // Section 2: Number of incidents (2 rows with 5 columns each)
    tableData.push([
      { content: '2', rowSpan: 2 },
      { content: 'Number of incidents (Today)', colSpan: 3 },
      getSafeValue(report.no_of_incidents_today || report.no_of_incidents)
    ]);
    tableData.push([
      { content: 'Number of incidents (Cumulative)', colSpan: 3 },
      getSafeValue(report.no_of_incidents_cum)
    ]);
    
    // Section 3: Address
    tableData.push([
      '3',
      'Addresses of the places of incidents',
      { content: getSafeStringValue(report.address), colSpan: 3 }
    ]);
    
    // Section 4: Number of deaths (4 rows, 5 columns each)
    tableData.push([
      { content: '4', rowSpan: 4 },
      { content: 'Number of deaths', colSpan: 2, styles: { fontStyle: 'bold' } },
      { content: 'Today', styles: { fontStyle: 'bold', halign: 'center' } },
      { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center' } }
    ]);
    tableData.push([
      { content: 'Human', colSpan: 2 },
      getSafeValue(report.no_of_human_deaths_today || report.no_of_human_deaths),
      getSafeValue(report.no_of_human_deaths_cum)
    ]);
    tableData.push([
      { content: 'Cattle', colSpan: 2 },
      getSafeValue(report.no_of_cattle_deaths_today || report.no_of_cattle_deaths),
      getSafeValue(report.no_of_cattle_deaths_cum)
    ]);
    tableData.push([
      { content: 'Poultry', colSpan: 2 },
      getSafeValue(report.no_of_poultry_deaths_today || report.no_of_poultry_deaths),
      getSafeValue(report.no_of_poultry_deaths_cum)
    ]);
    
    // Section 5: Number of injured
    tableData.push([
      { content: '5', rowSpan: 4 },
      { content: 'Number of injured', colSpan: 2, styles: { fontStyle: 'bold' } },
      { content: 'Today', styles: { fontStyle: 'bold', halign: 'center' } },
      { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center' } }
    ]);
    tableData.push([
      { content: 'Human', colSpan: 2 },
      getSafeValue(report.no_of_human_injured_today || report.no_of_human_injured),
      getSafeValue(report.no_of_human_injured_cum)
    ]);
    tableData.push([
      { content: 'Cattle', colSpan: 2 },
      getSafeValue(report.no_of_cattle_injured_today || report.no_of_cattle_injured),
      getSafeValue(report.no_of_cattle_injured_cum)
    ]);
    tableData.push([
      { content: 'Poultry', colSpan: 2 },
      getSafeValue(report.no_of_poultry_injured_today || report.no_of_poultry_injured),
      getSafeValue(report.no_of_poultry_injured_cum)
    ]);
    
    // Section 6: Ex-gratia details
    tableData.push([
      { content: '6', rowSpan: 4 },
      { content: 'Ex-gratia details', colSpan: 2, styles: { fontStyle: 'bold' } },
      { content: 'Today', styles: { fontStyle: 'bold', halign: 'center' } },
      { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center' } }
    ]);
    tableData.push([
      { content: 'Number of deceased for whom payment of ex-gratia done', colSpan: 2 },
      getSafeValue(report.no_of_deceased_pay_exgra_done_today || report.no_of_deceased_pay_exgra_done),
      getSafeValue(report.no_of_deceased_pay_exgra_done_cum)
    ]);
    tableData.push([
      { content: 'Number of dead cattles for which payment done', colSpan: 2 },
      getSafeValue(report.no_of_dead_cattles_pay_done_today || report.no_of_dead_cattles_pay_done),
      getSafeValue(report.no_of_dead_cattles_pay_done_cum)
    ]);
    tableData.push([
      { content: 'Number of dead poultry for which payment done', colSpan: 2 },
      getSafeValue(report.no_of_dead_poultry_pay_done_today || report.no_of_dead_poultry_pay_done),
      getSafeValue(report.no_of_dead_poultry_pay_done_cum)
    ]);
    
// Section 7: Number of damaged houses
tableData.push([
  { content: '7', rowSpan: 7 },
  { content: 'Number of damaged houses', colSpan: 2, styles: { fontStyle: 'bold' } },
  { content: 'Today', styles: { fontStyle: 'bold', halign: 'center' } },
  { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center' } }
]);
tableData.push([
  { content: 'Concrete', rowSpan: 2 },
  'Complete',
  getSafeValue(report.concrete_complete_today || report.concrete_complete),
  getSafeValue(report.concrete_complete_cum)
]);
tableData.push([
  'Partial',
  getSafeValue(report.concrete_partial_today || report.concrete_partial),
  getSafeValue(report.concrete_partial_cum)
]);
tableData.push([
  { content: 'Kutcha', rowSpan: 2 },
  'Complete',
  getSafeValue(report.kutcha_complete_today || report.kutcha_complete),
  getSafeValue(report.kutcha_complete_cum)
]);
tableData.push([
  'Partial',
  getSafeValue(report.kutcha_partial_today || report.kutcha_partial),
  getSafeValue(report.kutcha_partial_cum)
]);
// ✅ FIX: Changed colSpan from 1 to 2
tableData.push([
  { content: 'Cottage/Hut', colSpan: 2 },
  getSafeValue(report.cottage_hut_today || report.cottage_hut),
  getSafeValue(report.cottage_hut_cum)
]);
// ✅ FIX: Changed colSpan from 1 to 2
tableData.push([
  { content: 'Total number of damaged houses', colSpan: 2 },
  getSafeValue(report.total_today || report.total),
  getSafeValue(report.total_cum)
]);

// Section 8: Number of damaged houses for which payment done
tableData.push([
  { content: '8', rowSpan: 7 },
  { content: 'Number of damaged houses for which payment done', colSpan: 2, styles: { fontStyle: 'bold' } },
  { content: 'Today', styles: { fontStyle: 'bold', halign: 'center' } },
  { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center' } }
]);
tableData.push([
  { content: 'Concrete', rowSpan: 2 },
  'Complete',
  getSafeValue(report.concrete_complete_pay_done_today),
  getSafeValue(report.concrete_complete_pay_done_cum)
]);
tableData.push([
  'Partial',
  getSafeValue(report.concrete_partial_pay_done_today),
  getSafeValue(report.concrete_partial_pay_done_cum)
]);
tableData.push([
  { content: 'Kutcha', rowSpan: 2 },
  'Complete',
  getSafeValue(report.kutcha_complete_pay_done_today),
  getSafeValue(report.kutcha_complete_pay_done_cum)
]);
tableData.push([
  'Partial',
  getSafeValue(report.kutcha_partial_pay_done_today),
  getSafeValue(report.kutcha_partial_pay_done_cum)
]);
// ✅ FIX: Changed colSpan from 1 to 2
tableData.push([
  { content: 'Cottage/Hut', colSpan: 2 },
  getSafeValue(report.cottage_hut_pay_done_today),
  getSafeValue(report.cottage_hut_pay_done_cum)
]);
// ✅ FIX: Changed colSpan from 1 to 2
tableData.push([
  { content: 'Total number of damaged houses for which payment done', colSpan: 2 },
  getSafeValue(report.today_pay_done_today),
  getSafeValue(report.today_pay_done_cum)
]);

    
    // Section 9: Cattle shed details
    tableData.push([
      { content: '9', rowSpan: 4 },
      { content: 'Cattle shed details and Compensation for damaged house/hut/cattle shed', colSpan: 2, styles: { fontStyle: 'bold' } },
      { content: 'Today', styles: { fontStyle: 'bold', halign: 'center' } },
      { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center' } }
    ]);
    tableData.push([
      { content: 'Number of damaged cattle sheds', colSpan: 2 },
      getSafeValue(report.no_of_dam_cattle_sheds_today || report.no_of_dam_cattle_sheds),
      getSafeValue(report.no_of_dam_cattle_sheds_cum)
    ]);
    tableData.push([
      { content: 'Number of damaged cattle sheds for which payment done', colSpan: 2 },
      getSafeValue(report.no_of_dam_cattle_sheds_pay_done_today),
      getSafeValue(report.no_of_dam_cattle_sheds_pay_done_cum)
    ]);
    tableData.push([
      { content: 'Amount paid against damaged house/hut/cattle shed (Rs.)', colSpan: 2 },
      getSafeValue(report.amount_paid_dam_hous_hut_cattle_today),
      getSafeValue(report.amount_paid_dam_hous_hut_cattle_cum)
    ]);
    
    // Section 10: Crop information
    tableData.push([
      { content: '10', rowSpan: 4 },
      'Crop Type',
      { content: getSafeStringValue(report.crop_type), colSpan: 3 }
    ]);
    tableData.push([
      { content: 'Damaged Crop', colSpan: 2, styles: { fontStyle: 'bold' } },
      { content: 'Today', styles: { fontStyle: 'bold', halign: 'center' } },
      { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center' } }
    ]);
    tableData.push([
      { content: 'Area of damaged crop (in acre)', colSpan: 2 },
      getSafeValue(report.damaged_crop_area_affected_today || report.damaged_crop_area_affected),
      getSafeValue(report.damaged_crop_area_affected_cum)
    ]);
    tableData.push([
      { content: 'Estimated value of damaged crops (Rs.)', colSpan: 2 },
      getSafeValue(report.estimated_value_of_damaged_crops_today),
      getSafeValue(report.estimated_value_of_damaged_crops_cum)
    ]);
    
    // Section 11: Relief distribution
    tableData.push([
      { content: '11', rowSpan: 7 },
      { content: 'Relief Distribution', colSpan: 2, styles: { fontStyle: 'bold' } },
      { content: 'Today', styles: { fontStyle: 'bold', halign: 'center' } },
      { content: 'Cumulative', styles: { fontStyle: 'bold', halign: 'center' } }
    ]);
    tableData.push([
      { content: 'Amount paid under the agriculture input subsidy (Rs)', colSpan: 2 },
      getSafeValue(report.amount_paid_agricultural_input_sub_today),
      getSafeValue(report.amount_paid_agricultural_input_sub_cum)
    ]);
    tableData.push([
      { content: 'Area of crop for which agriculture input subsidy paid (in acre)', colSpan: 2 },
      getSafeValue(report.area_crop_agricultural_input_sub_paid_today),
      getSafeValue(report.area_crop_agricultural_input_sub_paid_cum)
    ]);
    tableData.push([
      { content: 'Total amount of GR paid @Rs. 7000 per family', colSpan: 2 },
      getSafeValue(report.total_amount_gr_paid_7k_per_fam_today),
      getSafeValue(report.total_amount_gr_paid_7k_per_fam_cum)
    ]);
    tableData.push([
      { content: 'Total amount paid for cloth/utensils etc., @5000 per family (Rs)', colSpan: 2 },
      getSafeValue(report.total_amount_cloth_paid_5k_per_fam_today),
      getSafeValue(report.total_amount_cloth_paid_5k_per_fam_cum)
    ]);
    tableData.push([
      { content: 'Number of polythene sheets distributed', colSpan: 2 },
      getSafeValue(report.number_of_polythene_sheets_distributed_today),
      getSafeValue(report.number_of_polythene_sheets_distributed_cum)
    ]);
    tableData.push([
      { content: 'Quantity of Dry ration distributed (Kg.)', colSpan: 2 },
      getSafeValue(report.quantity_dry_ration_distributed_today),
      getSafeValue(report.quantity_dry_ration_distributed_cum)
    ]);
    
    // Section 12: Remarks
    tableData.push([
      '12',
      'Remarks',
      { content: getSafeStringValue(report.remarks), colSpan: 3 }
    ]);
    
    // ✅ Generate PDF table with FIXED column widths
// ✅ Generate PDF table - MATCHING WEBUI (No bold, top alignment)
autoTable(doc, {
  body: tableData,
  startY: 190,
  theme: 'grid',
  columnStyles: {
    0: { cellWidth: 25, valign: 'top' },   // ✅ S.No - align top
    1: { cellWidth: 120, valign: 'top' },  // ✅ Label - align top
    2: { cellWidth: 180, valign: 'top' },  // ✅ Sub-label - align top
    3: { cellWidth: 110, valign: 'top' },  // ✅ Today - align top
    4: { cellWidth: 110, valign: 'top' }   // ✅ Cumulative - align top
  },
  bodyStyles: {
    lineColor: [0, 0, 0],
    lineWidth: 0.1,
    font: 'Helvetica',
    fontSize: 9,
    cellPadding: 3,
    valign: 'top',      // ✅ FIXED: Changed from middle to top
    fontStyle: 'normal' // ✅ FIXED: Force normal (no bold)
  },
  styles: {
    font: 'Helvetica',
    fontSize: 9,
    halign: 'left',
    valign: 'top',      // ✅ FIXED: Changed from middle to top
    overflow: 'linebreak',
    fontStyle: 'normal' // ✅ FIXED: Force normal (no bold)
  },
  // ✅ NEW: Callback to REMOVE all bold formatting (match WebUI)
  didParseCell: function (data) {
    // Force all cells to use normal font style (no bold)
    data.cell.styles.fontStyle = 'normal';
    
    // Keep center alignment for specific cells if needed
    if (data.cell.raw && typeof data.cell.raw === 'object' && data.cell.raw.styles) {
      if (data.cell.raw.styles.halign === 'center') {
        data.cell.styles.halign = 'center';
      }
    }
  },
  margin: { left: 10, right: 10, top: 190 }
});

    
    // Footer section
    const footerY = pageHeight - 80;
    doc.setFontSize(10);
    doc.setFont('Helvetica', 'normal');
    doc.text('Letter No.: ', 50, footerY);
    doc.text('Date: ', 50, footerY + 20);
    doc.text('Copy: ', 50, footerY + 40);
    
    doc.text('Signature of District Officers/ ', 320, footerY);
    doc.text('Additional District Magistrate(ADM)(Disaster Management)/', 320, footerY + 20);
    doc.text('Officer in Charge(District Disaster Management)/', 320, footerY + 40);
    doc.setFont('Helvetica', 'bold');
    doc.text(`[${districtName} DISTRICT]`, 320, footerY + 60);
    
    // Save PDF
    const finalFilename = generateISTFilename('fire_daily_report') + '.pdf';
    doc.save(finalFilename);
    console.log('✅✅✅ Fire Daily Report PDF downloaded:', finalFilename);
    
  } catch (error) {
    console.error('❌ Fire Daily Report PDF generation failed:', error);
    console.error('Stack:', error.stack);
    alert(`PDF Generation Error: ${error.message}`);
  }
};

// ... (rest of the functions remain the same)

/**
 * FIRE COMMUNITY KITCHEN DAILY REPORT PDF
 * Matches WebUI format exactly
 */
/**
 * FIRE COMMUNITY KITCHEN DAILY REPORT PDF
 * ✅ FIXED: Matches WebUI format with correct headers and spacing
 */
export const handleFireCommunityKitchenPDF = async (message, format, rawData) => {
  console.log('🔥 Fire Community Kitchen Report PDF generation started');
  console.log('📊 Raw Data:', JSON.stringify(rawData, null, 2));
  
  if (format !== 'pdf') {
    alert('Fire Community Kitchen Report only supports PDF format');
    return;
  }
  
  try {
    const doc = new jsPDF('l', 'pt', 'a4'); // Landscape orientation
    
    const reportDate =
      rawData?.reported_on ||
      rawData?.reported_on_iso ||
      message.debug?.tool_result?.parameters?.reported_on ||
      message.debug?.parameters?.reported_on ||
      new Date().toISOString().split('T')[0];
    
    const displayDate = formatDateToDDMMYYYY(reportDate);
    console.log('📅 Report Date:', displayDate);
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const centerX = pageWidth / 2;
    
    // Load logo
    let logoImg = null;
    try {
      logoImg = await loadLogo(govtLogo);
    } catch (e) {
      console.warn('⚠️ Logo load failed');
    }
    
    if (logoImg) {
      const logoWidth = 60;
      const logoHeight = 60;
      doc.addImage(logoImg, 'JPEG', centerX - logoWidth / 2, 20, logoWidth, logoHeight);
    }
    
    // Header section
    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(13);
    doc.text('Govt. of ABC State', centerX, 95, { align: 'center' });
    doc.text('Department Name', centerX, 115, { align: 'center' });
    doc.text(`Fire Community Kitchen Report (Dated on ${displayDate})`, centerX, 135, { align: 'center' });
    
    // Extract data
    let rows = [];
    if (rawData?.fire_ck && Array.isArray(rawData.fire_ck)) {
      rows = rawData.fire_ck;
    } else if (Array.isArray(rawData)) {
      rows = rawData;
    }
    
    // Merge with cumulative data
    if (rawData?.fire_ck_cumm && Array.isArray(rawData.fire_ck_cumm)) {
      rows = rows.map(row => {
        const cummData = rawData.fire_ck_cumm.find(cumm => cumm.region_id === row.region_id);
        return {
          ...row,
          till_mrng_food: cummData?.till_mrng_food ?? 'NA',
          till_evng_food: cummData?.till_evng_food ?? 'NA',
          till_today_total: cummData?.till_today_total ?? 'NA'
        };
      });
    }
    
    console.log('📊 Rows:', rows.length);
    
    const districtName = extractDistrictName(rawData, rows);
    
    // ✅ FIX 1: Correct table headers matching WebUI EXACTLY
    const tableHeaders = [
      'S.No.',
      'Block Name',
      'Today Number of Kitchen Running',
      'Lunch (Today)',
      'Dinner (Today)',
      'Total (Today)',
      'Lunch (Till Yesterday)',
      'Dinner (Till Yesterday)',
      'Total (Till Yesterday)'
    ];
    
    // Table data
    const tableData = rows.map((row, idx) => [
      String(idx + 1),
      getSafeStringValue(row.block_name || row.blockname),
      getSafeValue(row.kitchens_running || row.no_of_kitchens_run || row.today_no_of_kitchens_run),
      getSafeValue(row.lunch_today || row.mrng_food || row.today_mrng_food),
      getSafeValue(row.dinner_today || row.evng_food || row.today_evng_food),
      getSafeValue(row.total_today || row.today_total),
      getSafeValue(row.till_mrng_food),
      getSafeValue(row.till_evng_food),
      getSafeValue(row.till_today_total)
    ]);
    
// ✅ FIX 1: Calculate totals - "Total" should be in Block Name column
const totals = [
  '',       // ✅ Empty S.No column (index 0)
  'Total',  // ✅ "Total" in Block Name column (index 1)
  tableData.reduce((sum, row) => sum + (Number(row[2]) || 0), 0),
  tableData.reduce((sum, row) => sum + (Number(row[3]) || 0), 0),
  tableData.reduce((sum, row) => sum + (Number(row[4]) || 0), 0),
  tableData.reduce((sum, row) => sum + (Number(row[5]) || 0), 0),
  tableData.reduce((sum, row) => sum + (Number(row[6]) || 0), 0),
  tableData.reduce((sum, row) => sum + (Number(row[7]) || 0), 0),
  tableData.reduce((sum, row) => sum + (Number(row[8]) || 0), 0)
];

tableData.push(totals);

// ✅ Generate PDF table with CORRECT spacing
autoTable(doc, {
  head: [tableHeaders],
  body: tableData,
  startY: 160,
  theme: 'grid',
  headStyles: {
    fillColor: [255, 255, 255],
    textColor: [0, 0, 0],
    fontStyle: 'normal',
    lineColor: [0, 0, 0],
    lineWidth: 0.1,
    halign: 'center',
    valign: 'middle'
  },
  bodyStyles: {
    lineColor: [0, 0, 0],
    lineWidth: 0.1,
    font: 'Helvetica',
    fontSize: 9,
    cellPadding: 3,
    halign: 'center',
    valign: 'middle',
    fontStyle: 'normal'
  },
  styles: {
    font: 'Helvetica',
    fontSize: 9,
    overflow: 'linebreak',
    fontStyle: 'normal'  // ✅ Ensure normal font by default
  },
  columnStyles: {
    0: { cellWidth: 35, halign: 'center' },
    1: { cellWidth: 100, halign: 'left' },
    2: { cellWidth: 80 },
    3: { cellWidth: 75 },
    4: { cellWidth: 75 },
    5: { cellWidth: 75 },
    6: { cellWidth: 75 },
    7: { cellWidth: 75 },
    8: { cellWidth: 75 }
  },
  // ✅ FIX 2: Remove bold styling from total row (keep normal like WebUI)
  didParseCell: function (data) {
    // Force all cells to stay normal font (no bold)
    data.cell.styles.fontStyle = 'normal';
  },
  margin: { top: 160, left: 20, right: 20 }
});

    
    // Footer section
    const footerY = pageHeight - 80;
    doc.setFontSize(10);
    doc.setFont('Helvetica', 'normal');
    doc.text('Letter No.: ', 50, footerY);
    doc.text('Date: ', 50, footerY + 20);
    doc.text('Copy: ', 50, footerY + 40);
    
    doc.text('Signature of District Officers/ ', 550, footerY);
    doc.text('Additional District Magistrate(ADM)(Disaster Management)/', 550, footerY + 20);
    doc.text('Officer in Charge(District Disaster Management)/', 550, footerY + 40);
    doc.setFont('Helvetica', 'bold');
    doc.text(`[${districtName} DISTRICT]`, 550, footerY + 60);
    
    // Save PDF
    const finalFilename = generateISTFilename('fire_community_kitchen_report') + '.pdf';
    doc.save(finalFilename);
    console.log('✅ Fire Community Kitchen PDF downloaded:', finalFilename);
    
  } catch (error) {
    console.error('❌ Fire Community Kitchen PDF generation failed:', error);
    console.error('Stack:', error.stack);
    alert(`PDF Generation Error: ${error.message}`);
  }
};



export const isFireDailyReport = (toolName) => {
  const fireReports = [
    'fire_daily_report',
    'firedailyreport',
    'fire_incident_report',
    'fireincidentreport',
    'fire_incident_daily_report',
    'fireincidentdailyreport',
    'fire_incident_daily_report_form',
    'fireincidentdailyreportform'
  ];
  
  const normalized = toolName?.toLowerCase().trim();
  console.log(`🔍 Checking if "${normalized}" is fire daily report`);
  const match = fireReports.includes(normalized);
  console.log(`  Result: ${match ? '✅ YES' : '❌ NO'}`);
  return match;
};

export const isFireCommunityKitchenReport = (toolName) => {
  const fireReports = [
    'fire_community_kitchen_report',
    'firecommunitykitchenreport',
    'fire_community_kitchen_daily_report',      // ✅ ADD THIS
    'firecommunitykitchendailyreport'           // ✅ ADD THIS
  ];
  return fireReports.includes(toolName?.toLowerCase());
};

export const isFireDailyReportStatus = (toolName) => {
  const fireStatusReports = [
    'fire_daily_report_status',
    'firedailyreportstatus',
    'firewave_daily_report_status',
    'firewavedailyreportstatus',
    'get_fire_mis_report'
  ];
  return fireStatusReports.includes(toolName?.toLowerCase());
};
/**
 * ✅ Fire MIS Report Detection
 */
export const isFireMISReport = (toolName) => {
  const misReports = [
    'fire_mis_district',
    'fire_mis_district_datewise',
    'fire_mis_state',
    'fire_mis_state_datewise',
    'get_fire_mis_report'
  ];
  const normalized = toolName?.toLowerCase().trim();
  return misReports.some(name => normalized.includes(name));
};

// ✅ UPDATE THIS FUNCTION - Add isFireMISReport to the check
export const isFireReport = (toolName) => {
  return isFireDailyReport(toolName) ||
         isFireDailyReportStatus(toolName) ||
         isFireCommunityKitchenReport(toolName) ||
         isFireMISReport(toolName);  
};